import { type MeDto, STOCK_COUNT_STATUSES, type StockCountDto } from '@bqcrm/shared';
import { Anchor, Badge, Button, Card, Drawer, Group, Modal, NumberInput, Select, Stack, Table, Text, Textarea, TextInput } from '@mantine/core';
import { IconClipboardList, IconPlus } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api } from '../../api';
import { can } from '../../auth';
import { ask } from '../../confirm';
import { PageHeader } from '../../components/PageHeader';
import { QueryState } from '../../components/QueryState';
import { EmptyState } from '../../components/ui';
import { useCatalogGroups } from '../../data';
import { formatDateTime, qtyPl } from '../../format';
import { notifyError, notifyOk } from '../../notify';
import { WarehouseSelect } from './common';

/** Inwentaryzacja (spis z natury): różnica „policzono − stan” trafia do dokumentu INW z przyczyną. */
export function CountsPage({ me }: { me: MeDto }) {
  const list = useQuery({ queryKey: ['stock-counts'], queryFn: () => api<StockCountDto[]>('/stock-counts') });
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const canFix = can(me, 'magazyn.korekty');
  return (
    <>
      <PageHeader
        title="Inwentaryzacja"
        description="Spis z natury magazynu albo grupy produktów. Po zamknięciu różnice zmieniają stan dokumentem INW — z przyczyną i w historii."
        actions={
          canFix && (
            <Button leftSection={<IconPlus size={16} />} onClick={() => setCreating(true)}>
              Nowa inwentaryzacja
            </Button>
          )
        }
      />
      <Card padding={0}>
        <QueryState isLoading={list.isLoading} error={list.error}>
          {list.data?.length === 0 ? (
            <EmptyState icon={<IconClipboardList size={24} />} title="Brak inwentaryzacji" />
          ) : (
            <Table.ScrollContainer minWidth={640}>
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Rozpoczęta</Table.Th>
                    <Table.Th>Magazyn / zakres</Table.Th>
                    <Table.Th ta="right">Policzono</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Dokument</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {list.data?.map((c) => (
                    <Table.Tr key={c.id} className="row-link" onClick={() => setOpenId(c.id)}>
                      <Table.Td>
                        {formatDateTime(c.createdAt)}
                        <Text size="xs" c="dimmed">
                          {c.createdByName ?? ''}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        {c.warehouseCode} · {c.groupName ?? 'cały magazyn'}
                      </Table.Td>
                      <Table.Td ta="right">
                        {c.countedLines} / {c.totalLines}
                      </Table.Td>
                      <Table.Td>
                        <Badge size="sm" variant="light" color={c.status === 'otwarta' ? 'blue' : c.status === 'zamknieta' ? 'green' : 'gray'}>
                          {STOCK_COUNT_STATUSES[c.status]}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        {c.documentId ? (
                          <Anchor component={Link} to={`/magazyn/dokumenty?dokument=${c.documentId}`} onClick={(e) => e.stopPropagation()}>
                            {c.documentNumber}
                          </Anchor>
                        ) : (
                          '—'
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </QueryState>
      </Card>
      <CreateCount opened={creating} onClose={() => setCreating(false)} onCreated={(id) => (setCreating(false), setOpenId(id))} />
      <CountDrawer id={openId} onClose={() => setOpenId(null)} canFix={canFix} />
    </>
  );
}

function CreateCount({ opened, onClose, onCreated }: { opened: boolean; onClose: () => void; onCreated: (id: string) => void }) {
  const [warehouseId, setWarehouseId] = useState<string | null>(null);
  const [groupId, setGroupId] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const groups = useCatalogGroups();
  const qc = useQueryClient();
  const create = useMutation({
    mutationFn: () => api<StockCountDto>('/stock-counts', { method: 'POST', body: { warehouseId, groupId, note } }),
    onSuccess: (c) => {
      notifyOk('Rozpoczęto inwentaryzację');
      void qc.invalidateQueries({ queryKey: ['stock-counts'] });
      onCreated(c.id);
    },
    onError: notifyError,
  });
  return (
    <Modal opened={opened} onClose={onClose} title="Nowa inwentaryzacja">
      <Stack>
        <WarehouseSelect label="Magazyn" value={warehouseId} onChange={setWarehouseId} />
        <Select label="Zakres" placeholder="cały magazyn (produkty ze stanem lub ruchem)" clearable data={(groups.data ?? []).map((g) => ({ value: g.id, label: `Grupa: ${g.name}` }))} value={groupId} onChange={setGroupId} />
        <TextInput label="Uwagi" value={note} onChange={(e) => setNote(e.currentTarget.value)} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button onClick={() => create.mutate()} loading={create.isPending} disabled={!warehouseId}>
            Rozpocznij spis
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function CountDrawer({ id, onClose, canFix }: { id: string | null; onClose: () => void; canFix: boolean }) {
  const q = useQuery({ queryKey: ['stock-count', id], queryFn: () => api<StockCountDto>(`/stock-counts/${id}`), enabled: !!id });
  const c = q.data;
  const [counted, setCounted] = useState<Record<string, number | null>>({});
  const [reason, setReason] = useState('');
  const [closing, setClosing] = useState(false);
  const qc = useQueryClient();
  useEffect(() => {
    setCounted(Object.fromEntries((c?.lines ?? []).map((l) => [l.itemId, l.counted])));
  }, [c?.id, c?.lines]);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['stock-counts'] });
    void qc.invalidateQueries({ queryKey: ['stock-count'] });
    void qc.invalidateQueries({ queryKey: ['stock'] });
  };
  const dirty = (c?.lines ?? []).filter((l) => (counted[l.itemId] ?? null) !== l.counted);
  const save = useMutation({
    mutationFn: () => api<StockCountDto>(`/stock-counts/${id}/lines`, { method: 'PUT', body: { lines: dirty.map((l) => ({ itemId: l.itemId, counted: counted[l.itemId] ?? null, note: l.note })) } }),
    onSuccess: () => (notifyOk('Zapisano spis'), refresh()),
    onError: notifyError,
  });
  const close = useMutation({
    mutationFn: () => api<StockCountDto>(`/stock-counts/${id}/close`, { method: 'POST', body: { reason } }),
    onSuccess: (r) => {
      notifyOk(r.documentNumber ? `Zamknięto — różnice w ${r.documentNumber}` : 'Zamknięto — bez różnic');
      setClosing(false);
      refresh();
    },
    onError: notifyError,
  });
  const cancel = useMutation({
    mutationFn: () => api(`/stock-counts/${id}/cancel`, { method: 'POST' }),
    onSuccess: () => (notifyOk('Anulowano inwentaryzację'), refresh()),
    onError: notifyError,
  });
  const open = c?.status === 'otwarta' && canFix;
  return (
    <Drawer opened={!!id} onClose={onClose} position="right" size="xl" title={c ? `Inwentaryzacja ${c.warehouseCode} · ${c.groupName ?? 'cały magazyn'}` : 'Inwentaryzacja'}>
      <QueryState isLoading={q.isLoading} error={q.error}>
        {c && (
          <Stack>
            <Text size="sm" c="dimmed">
              Wpisz policzone ilości. Różnica liczy się od stanu w chwili zamknięcia (ruchy w trakcie spisu są uwzględnione). Pozycje bez wpisu — bez zmian.
            </Text>
            <Table.ScrollContainer minWidth={560}>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Produkt</Table.Th>
                    <Table.Th ta="right">Stan teraz</Table.Th>
                    <Table.Th w={130}>Policzono</Table.Th>
                    <Table.Th ta="right">Różnica</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {c.lines?.map((l) => {
                    const v = counted[l.itemId] ?? null;
                    const diff = v === null ? null : Math.round((v - l.current) * 1000) / 1000;
                    return (
                      <Table.Tr key={l.itemId}>
                        <Table.Td>
                          <Text size="sm">{l.name}</Text>
                          <Text size="xs" c="dimmed">
                            {l.code}
                          </Text>
                        </Table.Td>
                        <Table.Td ta="right">
                          {qtyPl(l.current)} {l.unit}
                        </Table.Td>
                        <Table.Td>
                          <NumberInput size="xs" min={0} decimalScale={3} decimalSeparator="," hideControls disabled={!open} value={v ?? ''} onChange={(x) => setCounted((m) => ({ ...m, [l.itemId]: x === '' ? null : Number(x) }))} />
                        </Table.Td>
                        <Table.Td ta="right">
                          <Text size="sm" c={diff === null || diff === 0 ? 'dimmed' : diff < 0 ? 'red' : 'green'}>
                            {diff === null ? '—' : `${diff > 0 ? '+' : ''}${qtyPl(diff)}`}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
            {open && (
              <Group justify="flex-end">
                <Button
                  variant="subtle"
                  color="red"
                  onClick={async () => {
                    if (await ask('Spis zostanie porzucony bez zmiany stanów.', { title: 'Anulować inwentaryzację?', confirm: 'Anuluj spis', danger: true })) cancel.mutate();
                  }}
                >
                  Anuluj spis
                </Button>
                <Button variant="light" onClick={() => save.mutate()} loading={save.isPending} disabled={!dirty.length}>
                  Zapisz spis
                </Button>
                <Button onClick={() => setClosing(true)} disabled={dirty.length > 0 || !c.countedLines}>
                  Zamknij i rozlicz różnice
                </Button>
              </Group>
            )}
          </Stack>
        )}
      </QueryState>
      <Modal opened={closing} onClose={() => setClosing(false)} title="Zamknięcie inwentaryzacji">
        <Stack>
          <Textarea label="Przyczyna różnic" required autosize minRows={2} value={reason} onChange={(e) => setReason(e.currentTarget.value)} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setClosing(false)}>
              Anuluj
            </Button>
            <Button onClick={() => close.mutate()} loading={close.isPending} disabled={reason.trim().length < 5}>
              Zamknij spis
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Drawer>
  );
}
